import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import MiniFlip from "@/components/MiniFlip";
import DigestPanel from "@/components/DigestPanel";
import AdminEssayEditor from "@/components/AdminEssayEditor";
import BotDraftsPanel from "@/components/BotDraftsPanel";
import ReportsPanel from "@/components/ReportsPanel";
import TeamPanel from "@/components/TeamPanel";
import { can, isSuperadmin } from "@/lib/roles";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Plus, Archive, RotateCcw, MessageCircle, TrendingUp, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_CATEGORIES = ["Reality TV & Pop Culture", "Bollywood & OTT", "Cricket & Sports", "Influencers & Internet", "Dating & Gen Z", "Tech & Culture", "Money & Life", "Politics"];

export default function AdminPanel() {
  const { user, api, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    category: "Reality TV & Pop Culture", question: "",
    side_a_label: "", side_a_text: "",
    side_b_label: "", side_b_text: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchTopics = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/topics");
      setTopics(data);
    } catch (err) {
      if (err.response?.status === 403) navigate("/");
    } finally {
      setLoading(false);
    }
  }, [api, navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !can(user, "staff")) { navigate("/"); return; }
    fetchTopics();
  }, [user, authLoading, navigate, fetchTopics]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.question || !form.side_a_label || !form.side_b_label) {
      toast.error("Fill in all required fields"); return;
    }
    setSubmitting(true);
    try {
      await api.post("/admin/topics", form);
      toast.success("Debate created!");
      setShowForm(false);
      setForm({ category: "Reality TV & Pop Culture", question: "", side_a_label: "", side_a_text: "", side_b_label: "", side_b_text: "" });
      fetchTopics();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    } finally {
      setSubmitting(false);
    }
  };

  const handleArchive = async (topicId) => {
    try {
      await api.put(`/admin/topics/${topicId}/archive`);
      toast.success("Archived");
      fetchTopics();
    } catch (err) {
      toast.error("Failed to archive");
    }
  };

  const handleRestore = async (topicId) => {
    try {
      await api.put(`/admin/topics/${topicId}/restore`);
      toast.success("Restored");
      fetchTopics();
    } catch (err) {
      toast.error("Failed to restore");
    }
  };

  if (authLoading) {
    return <div className="max-w-4xl mx-auto px-4 py-16 text-center text-sm text-neutral" data-testid="admin-loading">Loading…</div>;
  }
  if (!user || !can(user, "staff")) return null;

  const canContent = can(user, "content");
  const canModerate = can(user, "moderate");
  const superadmin = isSuperadmin(user);

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-6" data-testid="admin-panel">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-neutral hover:text-dark transition-colors mb-4" data-testid="admin-back-link">
        <ArrowLeft className="w-4 h-4" /> Home
      </Link>

      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl sm:text-4xl text-dark tracking-tight flex items-center gap-2">
          <ShieldAlert className="w-7 h-7 text-view-a" /> ADMIN PANEL
        </h1>
        {canContent && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-view-a rounded-xl hover:bg-view-a-dark transition-colors shadow-sm"
            data-testid="toggle-create-form"
          >
            <Plus className="w-4 h-4" /> New Debate
          </button>
        )}
      </div>

      {/* Create form */}
      {canContent && showForm && (
        <form onSubmit={handleCreate} className="bg-white rounded-2xl shadow-card p-6 mb-6 space-y-4" data-testid="create-topic-form">
          <div>
            <Label className="text-xs font-medium text-neutral">Category</Label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-view-a/30"
              data-testid="topic-category-select"
            >
              {DEFAULT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs font-medium text-neutral">Question *</Label>
            <Input
              value={form.question}
              onChange={(e) => setForm({ ...form, question: e.target.value })}
              placeholder="Should India...?"
              className="mt-1 rounded-xl border-gray-200"
              data-testid="topic-question-input"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-3 p-4 bg-view-a-light/50 rounded-xl border border-view-a/20">
              <h3 className="text-xs font-bold text-view-a uppercase tracking-wider">View A</h3>
              <div>
                <Label className="text-xs text-neutral">Label *</Label>
                <Input value={form.side_a_label} onChange={(e) => setForm({ ...form, side_a_label: e.target.value })} placeholder="Support..." className="mt-1 rounded-xl border-gray-200" data-testid="side-a-label-input" />
              </div>
              <div>
                <Label className="text-xs text-neutral">Argument</Label>
                <textarea value={form.side_a_text} onChange={(e) => setForm({ ...form, side_a_text: e.target.value })} placeholder="The case for this side..." className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-view-a/30 resize-none" rows={3} data-testid="side-a-text-input" />
              </div>
            </div>
            <div className="space-y-3 p-4 bg-view-b-light/50 rounded-xl border border-view-b/20">
              <h3 className="text-xs font-bold text-view-b uppercase tracking-wider">View B</h3>
              <div>
                <Label className="text-xs text-neutral">Label *</Label>
                <Input value={form.side_b_label} onChange={(e) => setForm({ ...form, side_b_label: e.target.value })} placeholder="Oppose..." className="mt-1 rounded-xl border-gray-200" data-testid="side-b-label-input" />
              </div>
              <div>
                <Label className="text-xs text-neutral">Argument</Label>
                <textarea value={form.side_b_text} onChange={(e) => setForm({ ...form, side_b_text: e.target.value })} placeholder="The case for this side..." className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-view-b/30 resize-none" rows={3} data-testid="side-b-text-input" />
              </div>
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={submitting} className="px-6 py-2.5 text-sm font-semibold text-white bg-view-a rounded-xl hover:bg-view-a-dark transition-colors disabled:opacity-50" data-testid="create-topic-submit">
              {submitting ? "Creating..." : "Create Debate"}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="px-6 py-2.5 text-sm font-semibold text-neutral bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors" data-testid="cancel-create">
              Cancel
            </button>
          </div>
        </form>
      )}

      {superadmin && <TeamPanel api={api} />}

      {canContent && <BotDraftsPanel api={api} onPublished={fetchTopics} />}

      {canModerate && <ReportsPanel api={api} />}

      {superadmin && <DigestPanel />}

      {/* Topics list */}
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-24 bg-white rounded-xl shadow-card animate-pulse" />)}</div>
      ) : topics.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-card p-8 text-center"><p className="text-neutral text-sm">No topics yet.</p></div>
      ) : (
        <div className="space-y-3" data-testid="admin-topics-list">
          {topics.map((t) => (
            <div
              key={t.topic_id}
              className={`bg-white rounded-xl shadow-card p-4 transition-all ${!t.is_active ? "opacity-60" : ""}`}
              data-testid={`admin-topic-${t.topic_id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-semibold tracking-wider uppercase text-neutral bg-gray-100 px-2 py-0.5 rounded-full">{t.category}</span>
                    {!t.is_active && <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">ARCHIVED</span>}
                  </div>
                  <h3 className="text-sm font-semibold text-dark leading-snug">{t.question}</h3>
                </div>
                <div className="flex gap-1.5">
                  {canContent && (t.is_active ? (
                    <button
                      onClick={() => handleArchive(t.topic_id)}
                      className="p-2 text-neutral hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                      title="Archive"
                      data-testid={`archive-${t.topic_id}`}
                    >
                      <Archive className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      onClick={() => handleRestore(t.topic_id)}
                      className="p-2 text-neutral hover:text-success hover:bg-green-50 rounded-lg transition-colors"
                      title="Restore"
                      data-testid={`restore-${t.topic_id}`}
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-4 text-[11px] text-neutral mb-2">
                <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3" /> {(t.votes_a || 0) + (t.votes_b || 0)} votes</span>
                <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {t.comment_count || 0} comments</span>
              </div>
              <MiniFlip votesA={t.votes_a || 0} votesB={t.votes_b || 0} />
              {canContent && <AdminEssayEditor topicId={t.topic_id} api={api} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
